import React, { Dispatch, RefObject, SetStateAction, useEffect, useState } from 'react';
import cn from 'classnames';
import { useSelect } from 'downshift';

import { formatDropdownNames, getInStockValues } from 'helpers/ShoppablePostUtils';
import { capitalize } from 'helpers/index';
import ProductUtils from 'helpers/ProductUtils';
import { ProductStyle } from 'types/cloudCatalog';
import { InStockMap, ProductDetails, SelectedDimensions, SelectMenuOption } from 'types/influencer';
import { FormattedProductSizing } from 'reducers/detail/productDetail';
import { INFLUENCER_SHOPPABLE_POSTS_PAGE } from 'constants/amethystPageTypes';
import { makeSizeLegendHeadingFromState } from 'components/productdetail/stylepicker/AirplaneSeatSizing';
import { ProductNotifyModal } from 'components/productdetail/asyncProductPageModals';

import css from 'styles/components/influencer/shoppablePosts/sizePicker.scss';

interface SizePickerProps {
  setSelectedStockId: Dispatch<SetStateAction<string | undefined>>;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => void;
  sizing: FormattedProductSizing;
  colorId: string;
  selectedStyle: ProductStyle | undefined;
  productStyleDetails: ProductDetails;
  resetProductNotifyState: () => void;
}

const getOptionCSSClasses = (isHighLighted: boolean, isSelected: boolean, outOfStock: boolean) =>
  cn(css.dropdownOption, {
    [css.outOfStockOption]: outOfStock,
    [css.selectedOption]: isSelected,
    [css.highlightedOption]: !isSelected && isHighLighted
  });

export const SizePicker = ({
  setSelectedStockId,
  onAddToCart,
  sizing,
  colorId,
  selectedStyle,
  productStyleDetails,
  resetProductNotifyState
}: SizePickerProps) => {
  const { valuesSet, valueIdToName, dimensionsSet = [], dimensionIdToUnitId, dimensionIdToName, stockData, airplaneCache } = sizing;
  const dimensionsSetLength = dimensionsSet.length;

  const [selectedDimensions, setSelectedDimensions] = useState<SelectedDimensions>({
    ...productStyleDetails?.productData?.selectedSizing
  } as SelectedDimensions);
  const [inStockValues, setInStockValues] = useState<InStockMap>({});
  const [isAddToCartEnabled, setIsAddToCartEnabled] = useState(Object.keys(selectedDimensions).length === dimensionsSetLength);
  const [isSelectedDimensionsOOS, setIsSelectedDimensionsOOS] = useState(false);
  const [showOOSModal, setShowOOSModal] = useState(false);
  const [lowStockMessage, setLowStockMessage] = useState('');
  const selectorRefs = React.useRef<RefObject<HTMLSelectElement>[]>(dimensionsSet.map(() => React.createRef()));

  useEffect(() => {
    selectorRefs.current.map(ref => {
      if (ref && ref.current) {
        ref.current!.selectedIndex = 0;
      }
    });
    // reset the local state
    const selectedDimensions = { ...productStyleDetails?.productData?.selectedSizing } as SelectedDimensions;
    setSelectedDimensions(selectedDimensions);
  }, [selectedStyle]);

  useEffect(() => {
    if (Object.keys(selectedDimensions).length === dimensionsSetLength) {
      const stock = ProductUtils.getStockBySize(stockData, colorId, selectedDimensions);
      setSelectedStockId(stock?.id);
      setIsAddToCartEnabled(true);
      setIsSelectedDimensionsOOS(!stock || !parseInt(stock.onHand));
      setLowStockMessage(stock && parseInt(stock.onHand) < 10 ? `Only ${parseInt(stock.onHand)} left in stock!` : '');
    } else {
      setSelectedStockId(undefined);
      setIsAddToCartEnabled(false);
      setIsSelectedDimensionsOOS(false);
      setLowStockMessage('');
    }
  }, [selectedDimensions]);

  useEffect(() => {
    setInStockValues(getInStockValues(selectedDimensions, dimensionsSet, stockData, colorId));
  }, [valuesSet, stockData, selectedDimensions, colorId]);

  const selectSizingOption = (value: string, dimension: string, index: number) => {
    if (value === '') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [dimension]: _, ...dimensionRemoved } = selectedDimensions;
      setSelectedDimensions(dimensionRemoved);
    } else {
      const newSelectedDimensions = {
        ...selectedDimensions,
        [dimension]: value
      };
      setSelectedDimensions(newSelectedDimensions);

      const options = sizing.allUnits[index]!.values;
      const stock = ProductUtils.getStockBySize(stockData, colorId, newSelectedDimensions);
      setShowOOSModal(Object.keys(newSelectedDimensions).length === dimensionsSetLength && !(stock && parseInt(stock.onHand)));

      ProductUtils.generateProductDimensionSelectedAmethystEvent(
        dimensionIdToName[dimension],
        valueIdToName[value]!.value,
        INFLUENCER_SHOPPABLE_POSTS_PAGE,
        options!,
        newSelectedDimensions,
        dimension,
        value,
        sizing,
        colorId,
        selectedStyle!
      );
    }
  };

  const formatDimensionName = (dimensionName: string) => {
    if (dimensionName === 'size' && airplaneCache) {
      const actualDimensionName = makeSizeLegendHeadingFromState(airplaneCache);
      !actualDimensionName.startsWith("Men's") && !actualDimensionName.startsWith("Women's")
        ? (dimensionName = 'Size:')
        : (dimensionName = actualDimensionName);
    } else if (dimensionName === 'width') {
      dimensionName = `${dimensionName} Options:`;
    } else {
      dimensionName = `${dimensionName}:`;
    }
    return capitalize(dimensionName!);
  };

  const openProductNotifyModal = () => {
    setShowOOSModal(true);
  };

  const onProductNotifyModalClose = () => {
    setShowOOSModal(false);
    resetProductNotifyState();
  };

  const CreateDropdown = (dropdownOptions: SelectMenuOption[], dimensionName: string, dimension: string, index: number) => {
    const getDropdownValue = (item: SelectMenuOption | null) => (item ? item.value : '');
    const {
      isOpen,
      selectedItem,
      getLabelProps,
      getToggleButtonProps,
      getMenuProps,
      highlightedIndex,
      getItemProps,
      reset: resetSelectedMenuState
    } = useSelect<SelectMenuOption>({
      items: dropdownOptions,
      itemToString: getDropdownValue,
      defaultSelectedItem: dropdownOptions.length === 1 ? dropdownOptions[0] : undefined,
      onSelectedItemChange: selection => selection.selectedItem && selectSizingOption(selection.selectedItem.value, dimension, index),
      getA11ySelectionMessage: options => (options?.selectedItem?.label ? `${options.selectedItem.label} has been selected.` : 'Selected')
    });

    useEffect(() => {
      // reset selected item from dimensions upon switching styles
      resetSelectedMenuState();
    }, [selectedStyle]);

    return (
      <div>
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <label {...getLabelProps()}>{formatDimensionName(dimensionName!)}</label>
        <button type="button" className={css.dropdownSelect} {...getToggleButtonProps()}>
          {selectedItem ? selectedItem.label : `Select ${dimensionName}: `}
        </button>
        {isOpen && (
          <ul className={css.dropdownList} {...getMenuProps()}>
            {dropdownOptions.map((item, index) => {
              const { value, label, isInStock } = item;
              const isSelected = item.value === selectedItem?.value;
              const itemProps = {
                ...getItemProps({ item, index }),
                'aria-label': `${label}${!isInStock ? ' - Out Of Stock' : ''}`
              };
              return (
                <li
                  key={`${dimensionName}-${value}`}
                  className={getOptionCSSClasses(index === highlightedIndex, isSelected, !isInStock)}
                  {...itemProps}
                >
                  {label}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className={css.sizeChooserSection}>
      {dimensionsSet.map((dimension, index) => {
        const dimensionName = dimensionIdToName[dimension];
        const dimensionValues = valuesSet[dimension]![dimensionIdToUnitId[dimension]!]!;
        const dropdownOptions: SelectMenuOption[] = formatDropdownNames(dimensionValues, valueIdToName, inStockValues[dimension]);
        return (
          <div
            key={dimension}
            className={cn(
              css.sizeChooserControlWrapper,
              { [css.sizeChooserCompleteRow]: dimensionsSetLength === 2 && index === 0 }, // In mobile view first dimension should occupy full row when there are 2 possible dimensions in dimension set
              { [css.sizeChooserPartialRow]: (dimensionsSetLength === 2 && index === 1) || dimensionsSetLength === 1 } // In mobile view it should occupy partial row for second dimension when there are 2 possible dimensions in dimension set or for only one dimension in dimension set
            )}
          >
            {CreateDropdown(dropdownOptions, dimensionName!, dimension, index)}
          </div>
        );
      })}
      <div className={css.addToCartWrapper}>
        {lowStockMessage && <span className={css.stockMessage}>{lowStockMessage}</span>}
        <button
          onClick={isSelectedDimensionsOOS ? openProductNotifyModal : onAddToCart}
          type="button"
          tabIndex={!isAddToCartEnabled ? -1 : 0}
          className={cn(css.addToCart, { [css.disableAddToCart]: !isAddToCartEnabled })}
        >
          {isSelectedDimensionsOOS ? 'Out Of Stock' : 'Add To Cart'}
        </button>
      </div>
      {showOOSModal && (
        <ProductNotifyModal
          isOpen={showOOSModal}
          onClose={onProductNotifyModalClose}
          product={productStyleDetails?.productData?.detail}
          productId={productStyleDetails?.productId.toString()}
          colorId={colorId}
          selectedSizing={selectedDimensions}
        />
      )}
    </div>
  );
};

export default SizePicker;
